import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useVelvet } from "@/context/VelvetContext";
import { fmtDate } from "@/lib/calc";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, User as UserIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STAGES = ['Nuevo', 'Contactado', 'Calificado', 'Cotizado', 'Ganado', 'Perdido'];
const SOURCES = ['Instagram', 'WhatsApp', 'Referido', 'Web', 'Otro'];

const STAGE_COLOR = {
  Nuevo:       'bg-[hsl(var(--soft-blush))] text-foreground border-[hsl(var(--soft-blush-dark))]',
  Contactado:  'bg-[hsl(60_30%_92%)] text-foreground border-[hsl(60_20%_78%)]',
  Calificado:  'bg-[hsl(var(--muted-green))]/25 text-foreground border-[hsl(var(--muted-green-dark))]',
  Cotizado:    'bg-[hsl(220_25%_92%)] text-foreground border-[hsl(220_20%_78%)]',
  Ganado:      'bg-[hsl(140_40%_88%)] text-foreground border-[hsl(140_30%_60%)]',
  Perdido:     'bg-[hsl(var(--muted))] text-muted-foreground border-border',
};

function StageBadge({ stage }) {
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${STAGE_COLOR[stage] || 'bg-muted border-border'}`}>
      {stage}
    </span>
  );
}

export default function Leads() {
  const { profile } = useAuth();
  const { designers } = useVelvet(); // includes profiles list (all active)
  const [leads, setLeads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const canFilterOwner = ['admin', 'manager'].includes(profile?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/crm/leads');
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(`No se pudieron cargar los leads: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch staff (sales/admin/manager) for owner assignment (admin/manager only).
  useEffect(() => {
    if (!canFilterOwner) return;
    (async () => {
      try {
        // Reuse profiles via velvet context if available; else fetch via supabase
        const { supabase } = await import('@/lib/supabase');
        const { data } = await supabase.from('profiles').select('id, full_name, email, role').in('role', ['admin','manager','sales']);
        setStaff(data || []);
      } catch (e) { /* silent */ }
    })();
  }, [canFilterOwner]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (s && !((l.name || '').toLowerCase().includes(s) ||
                 (l.email || '').toLowerCase().includes(s) ||
                 (l.phone || '').toLowerCase().includes(s) ||
                 (l.company || '').toLowerCase().includes(s))) return false;
      if (stageFilter !== 'all' && l.stage !== stageFilter) return false;
      if (sourceFilter !== 'all' && l.source !== sourceFilter) return false;
      if (ownerFilter !== 'all' && l.owner_id !== ownerFilter) return false;
      return true;
    });
  }, [leads, q, stageFilter, sourceFilter, ownerFilter]);

  const create = async (data) => {
    try {
      await apiFetch('/crm/leads', { method: 'POST', body: JSON.stringify(data) });
      toast.success(`Lead "${data.name}" creado`);
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1400px]" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="velvet-eyebrow mb-3">CRM</div>
          <h1 className="velvet-page-title">Leads</h1>
          <p className="text-sm text-muted-foreground mt-3">Prospectos en cultivo hasta convertirse en cliente.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…"
                   className="pl-9 w-56 rounded-sm" data-testid="lead-search-input" />
          </div>
          <Button onClick={() => setAddOpen(true)}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="add-lead-btn">
            <Plus className="h-4 w-4 mr-1" /> Nuevo lead
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="lead-filters">
        <FilterSelect label="Etapa" value={stageFilter} onChange={setStageFilter}
                      options={[['all', 'Todas'], ...STAGES.map(s => [s, s])]} testid="filter-stage" />
        <FilterSelect label="Fuente" value={sourceFilter} onChange={setSourceFilter}
                      options={[['all', 'Todas'], ...SOURCES.map(s => [s, s])]} testid="filter-source" />
        {canFilterOwner && (
          <FilterSelect label="Responsable" value={ownerFilter} onChange={setOwnerFilter}
                        options={[['all', 'Todos'], ...staff.map(s => [s.id, s.full_name || s.email])]}
                        testid="filter-owner" />
        )}
      </div>

      <div className="velvet-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-foreground/15 text-left">
                <th className="px-6 py-3 velvet-eyebrow">Nombre</th>
                <th className="px-6 py-3 velvet-eyebrow">Contacto</th>
                <th className="px-6 py-3 velvet-eyebrow">Fuente</th>
                <th className="px-6 py-3 velvet-eyebrow">Etapa</th>
                <th className="px-6 py-3 velvet-eyebrow hidden lg:table-cell">Responsable</th>
                <th className="px-6 py-3 velvet-eyebrow hidden lg:table-cell">Notas</th>
                <th className="px-6 py-3 velvet-eyebrow hidden md:table-cell">Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-6 py-16 text-center text-muted-foreground">Cargando…</td></tr>
              )}
              {!loading && filtered.map((l) => (
                <tr key={l.id}
                    className="border-b border-border hover:bg-[hsl(var(--muted))]/60 cursor-pointer"
                    onClick={() => setSelected(l)}
                    data-testid={`lead-row-${l.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-serif text-lg flex items-center gap-2">
                      {l.name}
                      {l.customer_id && (
                        <span title="Convertido en cliente"
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] px-1 py-0.5 border border-[hsl(140_30%_50%)] bg-[hsl(140_40%_92%)]">
                          <Sparkles className="h-2.5 w-2.5" /> Cliente
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{l.company || '—'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{l.email || '—'}</div>
                    <div className="text-xs text-muted-foreground">{l.phone || '—'}</div>
                  </td>
                  <td className="px-6 py-4"><span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{l.source}</span></td>
                  <td className="px-6 py-4"><StageBadge stage={l.stage} /></td>
                  <td className="px-6 py-4 hidden lg:table-cell text-muted-foreground">{l.owner_name || '—'}</td>
                  <td className="px-6 py-4 hidden lg:table-cell velvet-money">{l.notes_count || 0}</td>
                  <td className="px-6 py-4 hidden md:table-cell text-muted-foreground text-xs">{fmtDate(l.updated_at)}</td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-16 text-center text-muted-foreground">Sin leads que coincidan.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddLeadDialog open={addOpen} onOpenChange={setAddOpen} onSubmit={create}
                     canAssignOwner={canFilterOwner} staff={staff} />
      {selected && (
        <LeadDetailDialog leadId={selected.id} onClose={() => setSelected(null)}
                          onChanged={load} canAssignOwner={canFilterOwner} staff={staff} />
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="velvet-eyebrow text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm h-9 w-40" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function AddLeadDialog({ open, onOpenChange, onSubmit, canAssignOwner, staff }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    source: 'Otro', stage: 'Nuevo', owner_id: '',
    estimated_value: '', occasion: '',
  });
  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });
  const submit = () => {
    const payload = { ...form };
    if (!payload.owner_id) delete payload.owner_id;
    if (payload.estimated_value === '') delete payload.estimated_value;
    else payload.estimated_value = Number(payload.estimated_value);
    onSubmit(payload);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">Nuevo lead</DialogTitle>
          <DialogDescription>Un nuevo prospecto en el jardín.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div><Label>Nombre *</Label><Input value={form.name} onChange={set('name')} className="rounded-sm" data-testid="new-lead-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={form.email} onChange={set('email')} className="rounded-sm" data-testid="new-lead-email" /></div>
            <div><Label>Teléfono</Label><Input value={form.phone} onChange={set('phone')} className="rounded-sm" data-testid="new-lead-phone" /></div>
          </div>
          <div><Label>Empresa</Label><Input value={form.company} onChange={set('company')} className="rounded-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fuente</Label>
              <Select value={form.source} onValueChange={set('source')}>
                <SelectTrigger className="rounded-sm" data-testid="new-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Etapa</Label>
              <Select value={form.stage} onValueChange={set('stage')}>
                <SelectTrigger className="rounded-sm" data-testid="new-lead-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ocasión</Label><Input value={form.occasion} onChange={set('occasion')} className="rounded-sm" /></div>
            <div><Label>Valor estimado ($)</Label><Input type="number" value={form.estimated_value} onChange={set('estimated_value')} className="rounded-sm" /></div>
          </div>
          {canAssignOwner && (
            <div>
              <Label>Responsable</Label>
              <Select value={form.owner_id} onValueChange={set('owner_id')}>
                <SelectTrigger className="rounded-sm" data-testid="new-lead-owner"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name || s.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!form.name.trim()} onClick={submit}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="save-lead-btn">
            Guardar lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Wraps the detail view in a dialog (routing kept simple for the block).
import LeadDetail from "./LeadDetail";
function LeadDetailDialog({ leadId, onClose, onChanged, canAssignOwner, staff }) {
  return (
    <Dialog open={!!leadId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto p-0" data-testid="lead-detail-dialog">
        <VisuallyHidden.Root>
          <DialogTitle>Detalle del lead</DialogTitle>
          <DialogDescription>Editar información del lead y sus notas.</DialogDescription>
        </VisuallyHidden.Root>
        <LeadDetail leadId={leadId} onClose={onClose} onChanged={onChanged}
                    canAssignOwner={canAssignOwner} staff={staff} />
      </DialogContent>
    </Dialog>
  );
}
